/**
 * Signup portal API client.
 * Thin fetch wrappers around the worker's public + portal endpoints.
 */

import type {
  ConsentDisclosure,
  IntakePayload,
  ProspectMe,
  Step2Payload,
} from './types';

const API_BASE = import.meta.env.VITE_API_URL;

async function apiFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string; [k: string]: any }> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const text = await resp.text();
  let body: any;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { success: false, error: `Server error (${resp.status})` };
  }
  if (!resp.ok && body.success !== false) {
    body.success = false;
    body.error = body.error || `Request failed (${resp.status})`;
  }
  return body;
}

// =============================================================================
// Public (no session needed)
// =============================================================================

export async function getConsentDisclosures() {
  return apiFetch<ConsentDisclosure[]>(`/public/consent-disclosures`);
}

export async function getAffiliateByCode(code: string) {
  return apiFetch<{ display_name: string }>(
    `/public/affiliate-by-code/${encodeURIComponent(code)}`
  );
}

export async function submitIntake(payload: IntakePayload) {
  return apiFetch(`/public/prospect-intake`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function requestLoginLink(email: string, turnstileToken: string) {
  return apiFetch(`/public/prospect-login`, {
    method: 'POST',
    body: JSON.stringify({ email, turnstile_token: turnstileToken }),
  });
}

export async function redeemAuthToken(token: string) {
  return apiFetch<{ success: boolean; reason?: string }>(
    `/public/prospect-portal/auth?t=${encodeURIComponent(token)}`,
    { method: 'GET' }
  );
}

// =============================================================================
// Prospect portal (cookie auth)
// =============================================================================

export async function getMe(): Promise<{ success: boolean; prospect?: ProspectMe; error?: string }> {
  return apiFetch(`/prospect-portal/me`) as any;
}

export async function submitStep2(payload: Step2Payload) {
  return apiFetch(`/prospect-portal/step2`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function uploadSs4(file: File) {
  const form = new FormData();
  form.append('ss4', file);
  const resp = await fetch(`${API_BASE}/prospect-portal/step2/ss4-upload`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await resp.text();
  let body: any;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { success: false, error: `Server error (${resp.status})` };
  }
  if (!resp.ok && body.success !== false) {
    body.success = false;
    body.error = body.error || `Upload failed (${resp.status})`;
  }
  return body;
}

export async function createPaymentIntent(): Promise<
  { success: boolean; client_secret?: string; amount_cents?: number; error?: string }
> {
  return apiFetch(`/prospect-portal/step3/payment-intent`, {
    method: 'POST',
  }) as any;
}

export async function logout() {
  return apiFetch(`/prospect-portal/logout`, { method: 'POST' });
}
